import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Flex,
  Icon,
  Select,
  Badge,
  useBreakpointValue,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  useColorModeValue,
  IconButton,
  Tooltip,
  useColorMode,
  SlideFade,

} from '@chakra-ui/react'
import { 
  InfoIcon, 
  CheckCircleIcon, 
  ChatIcon,
  HamburgerIcon,
  ViewIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoonIcon,
  SunIcon,
  RepeatIcon,
  TimeIcon,
  StarIcon,
  CloseIcon,
  ExternalLinkIcon,
  ArrowBackIcon,
  SettingsIcon,
} from '@chakra-ui/icons'
import { keyframes } from '@emotion/react'
import { useAuth } from '../contexts/AuthContext'
import { api } from '../services/api'
import { useSubmissionPolling } from '../hooks/useSubmissionPolling'
import { isContestProblem } from '../utils/contestUtils'
import CodeEditor, { CodeEditorHandle } from '../components/CodeEditor'
import ProblemDescription from '../components/ProblemDescription'
import SubmissionHistory from '../components/SubmissionHistory'
import StdoutDisplay from '../components/StdoutDisplay'
import ProblemEditorNavbar from '../components/ProblemEditorNavbar'
import CompilerExplorer from '../components/CompilerExplorer'
import ContestProblemEditor from './ContestProblemEditor'
import CommandPalette from '../components/CommandPalette'
import ResultsOverview from '../components/ResultsOverview'
import CorrectnessTestModule from '../components/CorrectnessTestModule'
import BenchmarkModule from '../components/BenchmarkModule'
import ProfilerModule from '../components/ProfilerModule'
import ParallelAnalysisModule from '../components/ParallelAnalysisModule'
import TestResults from '../components/TestResults'
import RunConfigurationTab from '../components/RunConfigurationTab'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { PRM_CombinedResponse, NewJobRunnerSubmission } from '../types/unified_models'
import { normalizeCombinedResponseInputs } from '../utils/normalizeResponse'
// Use public path for images under /ide/images


interface ProblemData {
  problem_id: string
  title: string
  inputs: string[]
  competitor_files: {
    read_write: string[]
    read_only: string[]
  }
  benchmark_config: any
  readme: string // Plain text
  files: {
    [filename: string]: {
      content: string | null
      encoding: string | null
      omitted?: boolean
    }
  }
  compiler_options?: string // Base64 encoded
}

// Helper function to safely decode base64 strings
const decodeBase64 = (input: string | null | undefined): string => {
  // Handle null/undefined inputs
  if (!input) {
    return ''
  }

  // If it's already decoded (plain text), return as-is
  // Check if it looks like base64 (basic heuristic)
  if (!isValidBase64Format(input)) {
    return input
  }
  
  try {
    const decoded = atob(input)
    return decoded
  } catch (error) {
    // Return original string if decoding fails
    return input
  }
}

// Helper function to check if a string looks like valid base64
const isValidBase64Format = (str: string): boolean => {
  // Basic base64 format check: should only contain base64 characters
  // and be properly padded
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/
  
  // Must be non-empty and have length that's a multiple of 4 when padded
  if (!str || str.length === 0) {
    return false
  }
  
  // Check if it matches base64 character set
  if (!base64Regex.test(str)) {
    return false
  }
  
  // Check if it's likely to be base64 (longer strings, contains mix of chars)
  // Very short strings or strings with only basic ASCII are likely plain text
  if (str.length < 8 || /^[a-zA-Z0-9\s\-_.]+$/.test(str)) {
    return false
  }
  
  return true
}

export default function ProblemEditor() {
  const { problemId } = useParams<{ problemId: string }>()
  const { isAuthenticated, getAccessToken } = useAuth()
  const toast = useToast()
  const [sourceCode, setSourceCode] = useState('')
  const sourceCodeRef = useRef('')
  const editorRef = useRef<CodeEditorHandle | null>(null)
  // Kept for potential future debouncing but not used now to avoid frequent re-renders
  // const editorChangeTimeoutRef = useRef<number | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionResponse, setSubmissionResponse] = useState<PRM_CombinedResponse | null>(null)
  const [submissionToken, setSubmissionToken] = useState<string | null>(null)
  const [keybindingMode, setKeybindingMode] = useState<'default' | 'vim' | 'emacs'>('default')
  
  // Analysis target states
  const [enableCilksan, setEnableCilksan] = useState(false)
  const [enableCilkscale, setEnableCilkscale] = useState(false)
  const [preset, setPreset] = useState<'basic' | 'standard' | 'full'>('standard')
  
  // Profiler state
  const [enableProfiler, setEnableProfiler] = useState(false)
  const [selectedProfilerInput, setSelectedProfilerInput] = useState<string>('')
  
  // Mobile drawer for tabs
  const { isOpen, onOpen, onClose } = useDisclosure()
  
  // Command palette state
  const { isOpen: isCommandPaletteOpen, onOpen: onCommandPaletteOpen, onClose: onCommandPaletteClose } = useDisclosure()
  
  // Collapsible panel states
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false)
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false)
  const [isRightPanelMaximized, setIsRightPanelMaximized] = useState(false)
  // Feature flag: temporarily disable Speedy helper UI
  const SPEEDY_FEATURE_ENABLED = false
  const [isSpeedyVisible, setIsSpeedyVisible] = useState(false)
  const [speedyWiggleKey, setSpeedyWiggleKey] = useState(0)

  // Rise + wiggle animation for Speedy
  const speedyRiseWiggle = keyframes`
    0% { transform: translateY(40px) rotate(0deg); opacity: 1; }
    12% { transform: translateY(0) rotate(-8deg); }
    28% { transform: translateY(0) rotate(8deg); }
    44% { transform: translateY(0) rotate(-6deg); }
    60% { transform: translateY(0) rotate(6deg); }
    78% { transform: translateY(0) rotate(-3deg); }
    100% { transform: translateY(0) rotate(0deg); }
  `
  
  // Subtle appear for the whole helper (fade + small scale)
  const speedyAppear = keyframes`
    0% { opacity: 0; transform: translateY(8px) scale(0.96); }
    100% { opacity: 1; transform: translateY(0) scale(1); }
  `
  


  
  // Results tab state
  const [selectedResultsTab, setSelectedResultsTab] = useState(0)
  
  // Track if content has been modified from default
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  
  // Color mode
  const { colorMode, toggleColorMode } = useColorMode()
  
  // Dark theme colors
  const bgColor = useColorModeValue('white', 'gray.900')
  const editorBgColor = useColorModeValue('white', 'gray.800')
  const panelBgColor = useColorModeValue('gray.50', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textColor = useColorModeValue('gray.800', 'gray.100')
  const mutedColor = useColorModeValue('gray.600', 'gray.400')
  const tabSelectedBg = useColorModeValue('white', 'gray.700')
  const tabHoverBg = useColorModeValue('gray.100', 'gray.700')
  // Speedy glow colors for light/dark modes
  const speedyGlowPrimary = useColorModeValue('rgba(0,0,0,0.28)', 'rgba(255,255,255,0.45)')
  const speedyGlowAccent = useColorModeValue('rgba(99,179,237,0.35)', 'rgba(99,179,237,0.55)')
  
  // Fixed panel widths
  const leftPanelWidth = 350
  const rightPanelWidth = 400
  const rightPanelMaxWidth = 640
  
  // Responsive breakpoints
  const isMobile = useBreakpointValue({ base: true, md: false })
  


  // Load analysis target settings from localStorage
  useEffect(() => {
    const cilksanKey = `${problemId}-cilksan-enabled`
    const savedCilksan = localStorage.getItem(cilksanKey)
    if (savedCilksan === 'enabled') {
      setEnableCilksan(true)
    }
    
    const cilkscaleKey = `${problemId}-cilkscale-enabled`
    const savedCilkscale = localStorage.getItem(cilkscaleKey)
    if (savedCilkscale === 'enabled') {
      setEnableCilkscale(true)
    }
  }, [problemId])

  // Load profiler setting from localStorage
  useEffect(() => {
    const profilerKey = `${problemId}-profiler-enabled`
    const savedSetting = localStorage.getItem(profilerKey)
    if (savedSetting === 'enabled') {
      setEnableProfiler(true)
    }
    
    const profilerInputKey = `${problemId}-profiler-input`
    const savedInput = localStorage.getItem(profilerInputKey)
    if (savedInput) {
      setSelectedProfilerInput(savedInput)
    }
  }, [problemId])

  const { data: problem, isLoading, error } = useQuery<ProblemData>(
    ['problem', problemId],
    async () => {
      const token = await getAccessToken()
      const response = await api.get(`/problems/${problemId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      return response.data
    },
    {
      enabled: !!problemId, // Allow loading for both authenticated and non-authenticated users
      onSuccess: (data) => {
        // Extract source code from solution.cpp or the first read_write file
        let sourceCode = ''
        
        // Try to get solution.cpp first
        if (data.files && data.files['solution.cpp'] && data.files['solution.cpp'].content) {
          sourceCode = data.files['solution.cpp'].content
        } else if (data.competitor_files && data.competitor_files.read_write.length > 0) {
          // Fallback to first read_write file
          const firstFile = data.competitor_files.read_write[0]
          if (firstFile && data.files && data.files[firstFile] && data.files[firstFile]?.content) {
            sourceCode = data.files[firstFile]?.content || ''
          }
        }
        
        // If still no source code, check for legacy format
        if (!sourceCode && (data as any).source) {
          sourceCode = decodeBase64((data as any).source)
        }
        
        // Check if there's saved content in localStorage first
        const storageKey = `${problemId}-editor-content`
        const savedContent = localStorage.getItem(storageKey)
        
        // Check if there's a saved submission response
        const responseStorageKey = `${problemId}-submission-response`
        const savedResponse = localStorage.getItem(responseStorageKey)
        
        if (savedContent) {
          // Use saved content if available
          setSourceCode(savedContent)
          sourceCodeRef.current = savedContent
          // Check if saved content is different from default
          setHasUnsavedChanges(savedContent !== sourceCode)
        } else {
          // Use default content from problem data
          setSourceCode(sourceCode)
          sourceCodeRef.current = sourceCode
          setHasUnsavedChanges(false)
        }
        
        if (savedResponse) {
          try {
            // Restore saved submission response
            const parsedResponse = JSON.parse(savedResponse)
            const normalized = normalizeCombinedResponseInputs(parsedResponse)
            setSubmissionResponse(normalized)
          } catch (error) {
            console.error('Failed to parse saved submission response:', error)
            // Clear invalid saved response
            localStorage.removeItem(responseStorageKey)
          }
        }
        
        // Auto-select first input for profiler if none selected and multiple inputs exist
        if (data.inputs && data.inputs.length > 1 && !selectedProfilerInput && data.inputs[0]) {
          setSelectedProfilerInput(data.inputs[0])
          const profilerInputKey = `${problemId}-profiler-input`
          localStorage.setItem(profilerInputKey, data.inputs[0])
        }
        
        // Debug: log problem configuration
        console.log('Problem configuration loaded:', {
          problem_id: data.problem_id,
          inputs: data.inputs,
          competitor_files: data.competitor_files,
          files_available: Object.keys(data.files || {}),
          benchmark_config: data.benchmark_config
        })
      },
    }
  )

  // Submission polling hook
  const { isPolling } = useSubmissionPolling({
    token: submissionToken,
    onComplete: (response: PRM_CombinedResponse) => {
      const normalized = normalizeCombinedResponseInputs(response)
      setSubmissionResponse(normalized)
      setSubmissionToken(null)
      setIsSubmitting(false)
      
      // Save submission response to localStorage for persistence
      if (problemId) {
        const responseStorageKey = `${problemId}-submission-response`
        localStorage.setItem(responseStorageKey, JSON.stringify(normalized))
      }
      
      // Auto-switch to results tab when submission completes
      setSelectedResultsTab(1) // Switch to Results tab
    },
    onError: (error: string) => {
      console.error('Submission polling error:', error)
      setSubmissionToken(null)
      setIsSubmitting(false)
      toast({
        title: 'Submission Error',
        description: error,
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  })
  


  const handleSubmit = async () => {
    // Check if user is authenticated before allowing submission
    if (!isAuthenticated) {
      toast({
        title: 'Authentication Required',
        description: 'Please log in to submit solutions.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!sourceCode.trim()) {
      toast({
        title: 'Empty Code',
        description: 'Please write some code before submitting.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsSubmitting(true)
    setSubmissionResponse(null)

    try {
      const token = await getAccessToken()
      
      // Encode the source code to base64
      const encodeBase64 = (str: string): string => {
        return btoa(unescape(encodeURIComponent(str)))
      }

      // Create the new exercise submission format
      const targets: ('correctness' | 'benchmark' | 'cilkscale' | 'cilksan' | 'perf-profiler')[] = ['correctness', 'benchmark']
      if (enableProfiler) {
        targets.push('perf-profiler')
      }
      if (enableCilksan) {
        targets.push('cilksan')
      }
      if (enableCilkscale) {
        targets.push('cilkscale')
      }

      // Determine input targets based on profiler selection
      let inputTargets = problem?.inputs || []
      if (enableProfiler && selectedProfilerInput) {
        // When profiler is enabled with specific input, use only that input
        // This affects all targets (correctness, benchmark, and profiler)
        inputTargets = [selectedProfilerInput]
      }

      const submissionRequest: NewJobRunnerSubmission = {
        submission_token: `submission_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        submission_type: 'exercise',
        problem_id: problemId!,
        submission: {
          targets: targets,
          input_targets: inputTargets,
          modified_files: [
            {
              path: 'solution.cpp',
              content: encodeBase64(editorRef.current?.getValue() ?? sourceCodeRef.current),
              encoding: 'base64'
            }
          ]
        }
      }

      console.log('🚀 SUBMISSION REQUEST DETAILS:')
      console.log('📝 Full request:', JSON.stringify(submissionRequest, null, 2))
      console.log('🎯 Selected input_targets:', inputTargets)
      console.log('📂 Problem available inputs:', problem?.inputs)
      console.log('🔧 All targets:', targets)
      console.log('🛠️ Analysis targets enabled:', {
        profiler: enableProfiler,
        cilksan: enableCilksan,
        cilkscale: enableCilkscale
      })
      if (enableProfiler) {
        console.log('⚡ Profiler enabled, selected input:', selectedProfilerInput || 'all inputs')
        console.log('❓ Expected behavior: All tests should run ONLY on:', inputTargets)
      }

      const response = await api.post(`/submissions`, submissionRequest, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      const { token: submission_token } = response.data
      setSubmissionToken(submission_token)
      
      toast({
        title: 'Submission Successful',
        description: 'Your code has been submitted and is being processed.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      console.error('Submission error:', error)
      setIsSubmitting(false)
      toast({
        title: 'Submission Failed',
        description: error.response?.data?.detail || 'Failed to submit code. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }
  
  // Save analysis target settings to localStorage
  const handleCilksanChange = useCallback((checked: boolean) => {
    setEnableCilksan(checked)
    const cilksanKey = `${problemId}-cilksan-enabled`
    localStorage.setItem(cilksanKey, checked ? 'enabled' : 'disabled')
  }, [problemId])

  const handleCilkscaleChange = useCallback((checked: boolean) => {
    setEnableCilkscale(checked)
    const cilkscaleKey = `${problemId}-cilkscale-enabled`
    localStorage.setItem(cilkscaleKey, checked ? 'enabled' : 'disabled')
  }, [problemId])

  // Save profiler setting to localStorage
  const handleProfilerChange = useCallback((checked: boolean) => {
    setEnableProfiler(checked)
    const profilerKey = `${problemId}-profiler-enabled`
    localStorage.setItem(profilerKey, checked ? 'enabled' : 'disabled')
  }, [problemId])

  // Save profiler input selection to localStorage
  const handleProfilerInputChange = useCallback((input: string) => {
    setSelectedProfilerInput(input)
    const profilerInputKey = `${problemId}-profiler-input`
    localStorage.setItem(profilerInputKey, input)
  }, [problemId])

  // Handle preset changes
  const handlePresetChange = useCallback((value: 'basic' | 'standard' | 'full') => {
    setPreset(value)
    switch (value) {
      case 'basic':
        // Basic: Only required tests (correctness + benchmark)
        setEnableCilksan(false)
        setEnableCilkscale(false)
        setEnableProfiler(false)
        break
      case 'standard':
        // Standard: Same as basic (recommended for most submissions)
        setEnableCilksan(false)
        setEnableCilkscale(false)
        setEnableProfiler(false)
        break
      case 'full':
        // Full Analysis: All tools enabled for comprehensive analysis
        setEnableCilksan(true)
        setEnableCilkscale(true)
        setEnableProfiler(true)
        break
    }
  }, [])




  // Memoized decoded content to avoid recomputing during re-renders
  const decodedReadme = useMemo(() => decodeBase64(problem?.readme), [problem?.readme])
  const decodedCompilerOptions = useMemo(() => decodeBase64(problem?.compiler_options), [problem?.compiler_options])

  // Stable handlers to reduce re-renders (must be before any conditional returns)
  const handleRestoreSubmission = useCallback((code: string, response: PRM_CombinedResponse | null) => {
    setSourceCode(code)
    sourceCodeRef.current = code
    setSubmissionResponse(response)
    
    // Save the restored submission response to localStorage
    if (response && problemId) {
      const responseStorageKey = `${problemId}-submission-response`
      localStorage.setItem(responseStorageKey, JSON.stringify(response))
    }
  }, [problemId])

  // Helper function to get original source code
  const getOriginalSourceCode = useCallback(() => {
    if (!problem) return ''
    
    // Try to get solution.cpp first
    if (problem.files && problem.files['solution.cpp'] && problem.files['solution.cpp'].content) {
      return problem.files['solution.cpp'].content
    } else if (problem.competitor_files && problem.competitor_files.read_write.length > 0) {
      // Fallback to first read_write file
      const firstFile = problem.competitor_files.read_write[0]
      if (firstFile && problem.files && problem.files[firstFile] && problem.files[firstFile]?.content) {
        return problem.files[firstFile]?.content || ''
      }
    }
    
    // If still no source code, check for legacy format
    if ((problem as any).source) {
      return decodeBase64((problem as any).source)
    }
    
    return ''
  }, [problem])

  const handleEditorChange = useCallback((val: string) => {
    sourceCodeRef.current = val
    // Save to localStorage for persistence across page reloads
    const storageKey = `${problemId}-editor-content`
    localStorage.setItem(storageKey, val)
    
    // Check if content is different from original
    const originalContent = getOriginalSourceCode()
    setHasUnsavedChanges(val !== originalContent)
  }, [problemId, getOriginalSourceCode])

  // Handle restoring to default content
  const handleRestoreToDefault = useCallback(() => {
    if (!problem) return
    
    const originalContent = getOriginalSourceCode()
    
    // Update the editor and state
    setSourceCode(originalContent)
    sourceCodeRef.current = originalContent
    
    // Clear the saved content from localStorage
    const storageKey = `${problemId}-editor-content`
    localStorage.removeItem(storageKey)
    
    // Clear the saved submission response from localStorage
    const responseStorageKey = `${problemId}-submission-response`
    localStorage.removeItem(responseStorageKey)
    
    // Reset submission response
    setSubmissionResponse(null)
    
    // Reset unsaved changes flag
    setHasUnsavedChanges(false)
    
    toast({
      title: 'Restored to Default',
      description: 'Editor content and results have been restored to the original problem template.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    })
  }, [problem, problemId, getOriginalSourceCode, toast])

  // Keyboard shortcuts - must be called before any conditional returns
  useKeyboardShortcuts([
    { key: 'Enter', ctrl: true, handler: handleSubmit },
    { key: 'k', ctrl: true, handler: onCommandPaletteOpen },
    { key: 's', ctrl: true, shift: true, handler: () => handleCilksanChange(!enableCilksan) },
    { key: 'c', ctrl: true, shift: true, handler: () => handleCilkscaleChange(!enableCilkscale) },
    { key: 'r', ctrl: true, shift: true, handler: () => handleProfilerChange(!enableProfiler) },
    { key: 't', ctrl: true, shift: true, handler: toggleColorMode },
    { key: 'd', ctrl: true, shift: true, handler: handleRestoreToDefault },
    { key: '\\', ctrl: true, handler: () => setIsLeftPanelCollapsed(!isLeftPanelCollapsed) },
    { key: ']', ctrl: true, handler: () => setIsRightPanelCollapsed(!isRightPanelCollapsed) },
    { key: 'm', ctrl: true, shift: true, handler: () => setIsRightPanelMaximized(prev => !prev) },
  ])

  // Check if this is a contest problem and redirect to contest editor
  // This must come AFTER all hooks to satisfy React's rules of hooks
  if (problemId && isContestProblem(problemId)) {
    return <ContestProblemEditor />
  }

  // Allow non-authenticated users to view problems
  // Authentication check removed to enable public problem viewing

  if (isLoading) {
    return (
      <Container maxW="container.md" py={8}>
        <Box display="flex" justifyContent="center" py={8}>
          <Spinner size="xl" />
        </Box>
      </Container>
    )
  }

  if (error || !problem) {
    return (
      <Container maxW="container.md" py={8}>
        <Alert status="error">
          <AlertIcon />
          Failed to load problem. Please try again.
        </Alert>
      </Container>
    )
  }

  // moved earlier
  
  // Command palette commands
  const commands = [
    {
      id: 'submit',
      name: 'Submit Solution',
      icon: CheckCircleIcon,
      shortcut: 'Ctrl+Enter',
      action: handleSubmit,
    },
    {
      id: 'toggle-cilksan',
      name: 'Toggle Cilksan Race Detection',
      icon: InfoIcon,
      shortcut: 'Ctrl+Shift+S',
      action: () => handleCilksanChange(!enableCilksan),
    },
    {
      id: 'toggle-cilkscale',
      name: 'Toggle Cilkscale Analysis',
      icon: RepeatIcon,
      shortcut: 'Ctrl+Shift+C',
      action: () => handleCilkscaleChange(!enableCilkscale),
    },
    {
      id: 'toggle-profiler',
      name: 'Toggle Performance Profiler',
      icon: TimeIcon,
      shortcut: 'Ctrl+Shift+R',
      action: () => handleProfilerChange(!enableProfiler),
    },
    {
      id: 'toggle-theme',
      name: colorMode === 'light' ? 'Switch to Dark Mode' : 'Switch to Light Mode',
      icon: colorMode === 'light' ? MoonIcon : SunIcon,
      shortcut: 'Ctrl+Shift+T',
      action: toggleColorMode,
    },
    {
      id: 'restore-default',
      name: 'Restore to Default Content',
      icon: ArrowBackIcon,
      shortcut: 'Ctrl+Shift+D',
      action: handleRestoreToDefault,
    },

    {
      id: 'toggle-left-panel',
      name: isLeftPanelCollapsed ? 'Show Left Panel' : 'Hide Left Panel',
      icon: ChevronLeftIcon,
      action: () => setIsLeftPanelCollapsed(!isLeftPanelCollapsed),
    },
    {
      id: 'toggle-right-panel',
      name: isRightPanelCollapsed ? 'Show Right Panel' : 'Hide Right Panel',
      icon: ChevronRightIcon,
      action: () => setIsRightPanelCollapsed(!isRightPanelCollapsed),
    },
  ]

  // No debounced state updates anymore; rely on editorRef/sourceCodeRef

  return (
    <Box h="100vh" display="flex" flexDirection="column" bg={bgColor} overflow="hidden">
      {/* Speedy preload (disabled with feature flag) */}
      {SPEEDY_FEATURE_ENABLED && (
        <Box as="img" src="/images/speedy_logo.png" alt="" display="none" />
      )}
      {/* Custom Problem Editor Navbar */}
      <ProblemEditorNavbar
        problemId={problemId!}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isPolling={isPolling}
        isAuthenticated={isAuthenticated}
      />

      {/* Authentication Notice for Non-Authenticated Users */}
      {!isAuthenticated && (
        <Box bg="blue.50" borderBottom="1px" borderColor="blue.200" px={4} py={2}>
          <HStack justify="center" spacing={2}>
            <InfoIcon color="blue.500" />
            <Text fontSize="sm" color="blue.700">
              You're viewing this problem as a guest. 
              <Button
                variant="link"
                colorScheme="blue"
                size="sm"
                ml={1}
                onClick={() => {
                  const apiUrl = import.meta.env.VITE_API_URL || (
                    import.meta.env.PROD 
                      ? '/auth'
                      : 'http://localhost:3000/auth'
                  )
                  const currentPath = window.location.pathname + window.location.search
                  window.location.href = `${apiUrl}/login?return_to=${encodeURIComponent(currentPath)}`
                }}
              >
                Log in to submit solutions
              </Button>
            </Text>
          </HStack>
        </Box>
      )}

      {/* Main Content */}
      <Flex 
        flex={1} 
        overflow="hidden" 
        minH={0} 
        h={isAuthenticated ? "calc(100vh - 48px)" : "calc(100vh - 88px)"}
        position="relative"
      >
        {/* Left Panel - Description/History/Analysis (Desktop only) */}
        {!isMobile && (
          <Box
            w={isLeftPanelCollapsed ? '48px' : `${leftPanelWidth}px`}
            transition="width 0.3s ease"
            borderRight="1px"
            borderColor={borderColor}
            bg={bgColor}
            display="flex"
            flexDirection="column"
            flexShrink={0}
            overflow="hidden"
          >
            {isLeftPanelCollapsed ? (
              <Box p={3}>
                <IconButton
                  aria-label="Expand left panel"
                  icon={<Icon as={ChevronRightIcon} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsLeftPanelCollapsed(false)}
                />
              </Box>
            ) : (
              <Tabs 
                isLazy
                lazyBehavior="keepMounted"
                variant="enclosed"
                colorScheme="blue"
                h="100%"
                display="flex"
                flexDirection="column"
                flex={1}
              >
                <HStack borderBottom="1px" borderColor={borderColor} bg={panelBgColor} px={3} py={2}>
                  <TabList flex={1} border="none">
                    <Tab 
                      fontSize="sm" 
                      fontWeight="semibold"
                      color={mutedColor}
                      _selected={{ 
                        bg: tabSelectedBg, 
                        color: "blue.600",
                        borderBottomColor: "transparent"
                      }}
                      _hover={{
                        bg: tabHoverBg,
                        color: textColor
                      }}
                    >
                      <Icon as={InfoIcon} mr={2} boxSize={4} />
                      Description
                    </Tab>
                    <Tab 
                      fontSize="sm" 
                      fontWeight="semibold"
                      color={mutedColor}
                      _selected={{ 
                        bg: tabSelectedBg, 
                        color: "green.600",
                        borderBottomColor: "transparent"
                      }}
                      _hover={{
                        bg: tabHoverBg,
                        color: textColor
                      }}
                    >
                      <Icon as={SettingsIcon} mr={2} boxSize={4} />
                      Configuration
                    </Tab>
                    <Tab 
                      fontSize="sm" 
                      fontWeight="semibold"
                      color={mutedColor}
                      _selected={{ 
                        bg: tabSelectedBg, 
                        color: "purple.600",
                        borderBottomColor: "transparent"
                      }}
                      _hover={{
                        bg: tabHoverBg,
                        color: textColor
                      }}
                    >
                      <Icon as={ChatIcon} mr={2} boxSize={4} />
                      History
                    </Tab>
                  </TabList>
                  <IconButton
                    aria-label="Collapse left panel"
                    icon={<Icon as={ChevronLeftIcon} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setIsLeftPanelCollapsed(true)}
                  />
                </HStack>

                <TabPanels flex={1} overflow="hidden" minH={0}>
                  {/* Description Tab */}
                  <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                    <ProblemDescription content={decodedReadme} />
                  </TabPanel>

                  {/* Run Configuration Tab */}
                  <TabPanel h="100%" overflow="hidden" p={0} display="flex" flexDirection="column" bg={bgColor}>
                    <RunConfigurationTab
                      enableCilksan={enableCilksan}
                      enableCilkscale={enableCilkscale}
                      enableProfiler={enableProfiler}
                      onCilksanChange={handleCilksanChange}
                      onCilkscaleChange={handleCilkscaleChange}
                      onProfilerChange={handleProfilerChange}
                      hasMultipleInputs={problem?.inputs ? problem.inputs.length > 1 : false}
                      profilerInput={selectedProfilerInput}
                      availableInputs={problem?.inputs || []}
                      onProfilerInputChange={handleProfilerInputChange}
                      preset={preset}
                      onPresetChange={handlePresetChange}
                      onRunAnalysis={handleSubmit}
                      isSubmitting={isSubmitting}
                      isAuthenticated={isAuthenticated}
                    />
                  </TabPanel>

                  {/* History Tab */}
                  <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                    <SubmissionHistory 
                      problemId={problemId!}
                      onRestoreSubmission={handleRestoreSubmission}
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </Box>
        )}

        {/* Center Panel - Code Editor (Prominent) */}
        <Box 
          flex={1} 
          display="flex" 
          flexDirection="column" 
          bg={editorBgColor} 
          minH={0}
          minW={isMobile ? "100%" : "400px"}
        >
          <Box 
            bg={panelBgColor} 
            borderBottom="1px" 
            borderColor={borderColor} 
            px={4} 
            py={3}
            flexShrink={0}
          >
            <HStack justify="space-between">
              <HStack spacing={3}>
                <Text fontSize="sm" fontWeight="semibold" color={textColor}>
                  Code Editor
                </Text>
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                  C++
                </Badge>
                {hasUnsavedChanges && (
                  <Badge colorScheme="orange" variant="solid" fontSize="xs">
                    Modified
                  </Badge>
                )}
              </HStack>
              <HStack spacing={2}>
                {/* Command Palette Button */}
                <Tooltip label="Command Palette (Ctrl+K)">
                  <IconButton
                    size="sm"
                    variant="ghost"
                    icon={<Icon as={ViewIcon} />}
                    onClick={onCommandPaletteOpen}
                    aria-label="Open command palette"
                  />
                </Tooltip>
                {/* Restore to Default Button */}
                <Tooltip label="Restore to Default Content (Ctrl+Shift+D)">
                  <IconButton
                    size="sm"
                    variant={hasUnsavedChanges ? "solid" : "ghost"}
                    icon={<Icon as={ArrowBackIcon} />}
                    onClick={handleRestoreToDefault}
                    aria-label="Restore to default content"
                    colorScheme="orange"
                    isDisabled={!hasUnsavedChanges}
                  />
                </Tooltip>
                {/* Run Analysis */}
                <Tooltip label={isAuthenticated ? "Run (Ctrl+Enter)" : "Login required to submit"}>
                  <Button
                    size="sm"
                    colorScheme={isAuthenticated ? "blue" : "gray"}
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    loadingText="Running"
                    isDisabled={!isAuthenticated}
                  >
                    {isAuthenticated ? "Run" : "Login to Run"}
                  </Button>
                </Tooltip>

                {SPEEDY_FEATURE_ENABLED && (
                <Button
                  size="sm"
                  colorScheme="pink"
                  onClick={() => {
                    setIsSpeedyVisible(true)
                    setSpeedyWiggleKey((k) => k + 1)
                  }}
                >
                  Ask Speedy!
                </Button>
                )}
                
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onOpen}
                  display={{ base: 'flex', lg: 'none' }}
                  px={2}
                  py={1}
                  fontSize="xs"
                >
                  <Icon as={HamburgerIcon} boxSize={4} />
                </Button>
                <Select
                  size="sm"
                  width="120px"
                  value={keybindingMode}
                  onChange={(e) => setKeybindingMode(e.target.value as 'default' | 'vim' | 'emacs')}
                  fontSize="xs"
                  bg={bgColor}
                  borderColor={borderColor}
                  color={textColor}
                  _hover={{ borderColor: "blue.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px blue.500" }}
                >
                  <option value="default">Default</option>
                  <option value="vim">Vim</option>
                  <option value="emacs">Emacs</option>
                </Select>
              </HStack>
            </HStack>
          </Box>
          <Box flex={1} p={0}>
            <CodeEditor
              ref={editorRef}
              value={sourceCode}
              onChange={handleEditorChange}
              language="cpp"
              height="100%"
              keybindingMode={keybindingMode}
            />
          </Box>
        </Box>

        {/* Right Panel - Results (Desktop Only) */}
        {!isMobile && (
          <Box 
            w={isRightPanelCollapsed ? '48px' : `${(isRightPanelMaximized ? rightPanelMaxWidth : rightPanelWidth)}px`}
            minW={isRightPanelCollapsed ? '48px' : `${(isRightPanelMaximized ? rightPanelMaxWidth : rightPanelWidth)}px`}
            maxW={isRightPanelCollapsed ? '48px' : `${(isRightPanelMaximized ? rightPanelMaxWidth : rightPanelWidth)}px`}
            transition="width 0.3s ease"
            borderLeft="1px" 
            borderColor={borderColor} 
            bg={bgColor} 
            display="flex"
            flexDirection="column"
            flexShrink={0}
            overflow="hidden"
          >
            {isRightPanelCollapsed ? (
              <Box p={3}>
                <IconButton
                  aria-label="Expand right panel"
                  icon={<Icon as={ChevronLeftIcon} />}
                  size="sm"
                  variant="ghost"
                  onClick={() => setIsRightPanelCollapsed(false)}
                />
              </Box>
            ) : (
              <VStack spacing={0} h="100%" overflow="hidden" minW={0}>
                {/* Results Tabs */}
                <Tabs 
                  isLazy
                  lazyBehavior="keepMounted"
                  index={selectedResultsTab}
                  onChange={setSelectedResultsTab}
                  variant="enclosed"
                  colorScheme="blue"
                  w="100%"
                  display="flex"
                  flexDirection="column"
                  flex={1}
                  overflow="hidden"
                  minW={0}
                >
                <TabList borderBottom="1px" borderColor={borderColor} bg={panelBgColor} flexShrink={0} minW={0}>
                  <Tab 
                    fontSize="xs" 
                    fontWeight="semibold"
                    color={mutedColor}
                    px={2}
                    _selected={{ 
                      bg: tabSelectedBg, 
                      color: "blue.600",
                      borderBottomColor: "transparent"
                    }}
                    _hover={{
                      bg: tabHoverBg,
                      color: textColor
                    }}
                  >
                    <Icon as={InfoIcon} mr={1} boxSize={3} />
                    Overview
                  </Tab>
                  <Tab 
                    fontSize="xs" 
                    fontWeight="semibold"
                    color={mutedColor}
                    px={2}
                    _selected={{ 
                      bg: tabSelectedBg, 
                      color: "green.600",
                      borderBottomColor: "transparent"
                    }}
                    _hover={{
                      bg: tabHoverBg,
                      color: textColor
                    }}
                  >
                    <Icon as={CheckCircleIcon} mr={1} boxSize={3} />
                    Tests
                  </Tab>
                  <Tab 
                    fontSize="xs" 
                    fontWeight="semibold"
                    color={mutedColor}
                    px={2}
                    _selected={{ 
                      bg: tabSelectedBg, 
                      color: "yellow.600",
                      borderBottomColor: "transparent"
                    }}
                    _hover={{
                      bg: tabHoverBg,
                      color: textColor
                    }}
                  >
                    <Icon as={StarIcon} mr={1} boxSize={3} />
                    Perf
                  </Tab>
                  <Tab 
                    fontSize="xs" 
                    fontWeight="semibold"
                    color={mutedColor}
                    px={2}
                    _selected={{ 
                      bg: tabSelectedBg, 
                      color: "purple.600",
                      borderBottomColor: "transparent"
                    }}
                    _hover={{
                      bg: tabHoverBg,
                      color: textColor
                    }}
                  >
                    <Icon as={RepeatIcon} mr={1} boxSize={3} />
                    Analysis
                  </Tab>
                  <Tab 
                    fontSize="xs" 
                    fontWeight="semibold"
                    color={mutedColor}
                    px={2}
                    _selected={{ 
                      bg: tabSelectedBg, 
                      color: "gray.600",
                      borderBottomColor: "transparent"
                    }}
                    _hover={{
                      bg: tabHoverBg,
                      color: textColor
                    }}
                  >
                    <Icon as={ViewIcon} mr={1} boxSize={3} />
                    Output
                  </Tab>
                  <HStack ml="auto" spacing={1}>
                    <Tooltip label={isRightPanelMaximized ? 'Restore width (Ctrl+Shift+M)' : 'Expand width (Ctrl+Shift+M)'}>
                      <IconButton
                        aria-label={isRightPanelMaximized ? 'Restore right panel width' : 'Expand right panel width'}
                        icon={<Icon as={isRightPanelMaximized ? ArrowBackIcon : ExternalLinkIcon} />}
                        size="sm"
                        variant={isRightPanelMaximized ? 'solid' : 'ghost'}
                        onClick={() => setIsRightPanelMaximized(prev => !prev)}
                      />
                    </Tooltip>
                    <Tooltip label="Collapse right panel (Ctrl+])">
                      <IconButton
                        aria-label="Collapse right panel"
                        icon={<Icon as={ChevronRightIcon} />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setIsRightPanelCollapsed(true)}
                      />
                    </Tooltip>
                  </HStack>
                </TabList>

                <TabPanels flex={1} overflow="hidden" display="flex" flexDirection="column" minW={0}>
                  {/* Overview Tab */}
                  <TabPanel flex={1} overflow="hidden" p={0} bg={bgColor}>
                    <Box w="100%" h="100%" overflowY="auto" overflowX="hidden" minW={0}>
                      <ResultsOverview 
                        response={submissionResponse} 
                        onNavigateToTab={(tabIndex) => {
                          setSelectedResultsTab(tabIndex)
                        }}
                      />
                    </Box>
                  </TabPanel>

                  {/* Tests Tab - Correctness Only */}
                  <TabPanel flex={1} overflow="hidden" p={0} bg={bgColor}>
                    <Box w="100%" h="100%" overflowY="auto" overflowX="hidden" p={4} minW={0}>
                      <CorrectnessTestModule correctness={submissionResponse?.correctness || null} />
                    </Box>
                  </TabPanel>

                  {/* Performance Tab - Benchmarks Only */}
                  <TabPanel flex={1} overflow="hidden" p={0} bg={bgColor}>
                    <Box w="100%" h="100%" overflowY="auto" overflowX="hidden" p={4} minW={0}>
                      <Box w="100%" maxW="100%" minW={0}>
                        <BenchmarkModule 
                          benchmark={submissionResponse?.benchmark || null} 
                          activeInput={selectedProfilerInput || null}
                          limitToActiveInput={Boolean(enableProfiler && selectedProfilerInput)}
                        />
                      </Box>
                    </Box>
                  </TabPanel>

                  {/* Analysis Tab - Cilksan, Cilkscale, Profiler */}
                  <TabPanel flex={1} overflow="hidden" p={0} bg={bgColor}>
                    <Box w="100%" h="100%" overflowY="auto" overflowX="hidden" p={4} minW={0}>
                      <VStack spacing={4} align="stretch" w="100%" minW={0}>
                        {submissionResponse?.profiler && (
                          <Box w="100%" overflow="hidden" minW={0}>
                            <ProfilerModule 
                              profiler={submissionResponse.profiler}
                              benchmark={submissionResponse.benchmark || null}
                            />
                          </Box>
                        )}
                        {(submissionResponse?.cilksan || submissionResponse?.cilkscale_benchmark) && (
                          <Box w="100%" overflow="hidden" minW={0}>
                            <ParallelAnalysisModule 
                              cilksan={submissionResponse?.cilksan || null} 
                              cilkscaleBenchmark={submissionResponse?.cilkscale_benchmark || null} 
                              sourceCode={sourceCode} 
                            />
                          </Box>
                        )}
                        {!submissionResponse?.profiler && !submissionResponse?.cilksan && !submissionResponse?.cilkscale_benchmark && (
                          <Box textAlign="center" py={8} color={mutedColor}>
                            <Text>No analysis tools were enabled for this submission.</Text>
                            <Text fontSize="sm">Enable analysis tools above before submitting.</Text>
                          </Box>
                        )}
                      </VStack>
                    </Box>
                  </TabPanel>

                  {/* Output Tab - stdout/stderr */}
                  <TabPanel flex={1} overflow="hidden" p={0} bg={bgColor}>
                    <Box w="100%" h="100%" overflowY="auto" overflowX="hidden" p={4} minW={0}>
                      <StdoutDisplay response={submissionResponse} />
                    </Box>
                  </TabPanel>
                </TabPanels>
              </Tabs>
              </VStack>
            )}
          </Box>
        )}
      </Flex>
      {/* Speedy Helper Overlay (disabled) */}
      {SPEEDY_FEATURE_ENABLED && (
        <SlideFade in={isSpeedyVisible} offsetY="10px" unmountOnExit>
          <Box
            position="fixed"
            right={6}
            bottom={6}
            zIndex={20}
          >
            <HStack
              key={`appear-${speedyWiggleKey}`}
              align="center"
              spacing={5}
              sx={{
                willChange: 'opacity, transform',
                animation: `${speedyAppear} 320ms ease-out`,
              }}
            >
              <Box
                bg={panelBgColor}
                border="1px"
                borderColor={borderColor}
                boxShadow="lg"
                borderRadius="2xl"
                px={8}
                py={6}
                position="relative"
                _after={{
                  content: '""',
                  position: 'absolute',
                  right: '-12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '0',
                  height: '0',
                  borderTop: '12px solid transparent',
                  borderBottom: '12px solid transparent',
                  borderLeft: `12px solid ${useColorModeValue('#E2E8F0', '#2D3748')}`,
                }}
              >
                <HStack spacing={5}>
                  <Text fontSize="lg" color={textColor}>
                    "I'm Speedy! Try to unroll your loops!"
                  </Text>
                  <IconButton
                    aria-label="Close Speedy hint"
                    size="md"
                    variant="ghost"
                    onClick={() => setIsSpeedyVisible(false)}
                    icon={<Icon as={CloseIcon} />}
                  />
                </HStack>
              </Box>
              <Image
                key={speedyWiggleKey}
                src={'/images/speedy_logo.png'}
                alt="Speedy"
                h="192px"
                w="auto"
                objectFit="contain"
                bg="transparent"
                sx={{
                  transformOrigin: 'bottom center',
                  animation: `${speedyRiseWiggle} 3000ms ease-out`,
                  animationDelay: '240ms',
                  willChange: 'transform',
                  filter: `drop-shadow(0 0 8px ${speedyGlowPrimary}) drop-shadow(0 0 16px ${speedyGlowAccent})`,
                }}
              />
            </HStack>
          </Box>
        </SlideFade>
      )}

      {/* Mobile Drawer for Tabs */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="full">
        <DrawerOverlay />
        <DrawerContent bg={bgColor}>
          <DrawerCloseButton />
          <DrawerHeader borderBottom="1px" borderColor={borderColor}>
            <VStack spacing={3} align="stretch">
              <Text color={textColor}>Problem Details</Text>
              <Text fontSize="sm" color={mutedColor}>
                Tool configuration has been moved to the main interface for easier access.
              </Text>
            </VStack>
          </DrawerHeader>
          <DrawerBody p={0}>
            <Tabs 
              variant="enclosed"
              colorScheme="blue"
              h="100%"
              display="flex"
              flexDirection="column"
            >
              <TabList borderBottom="1px" borderColor={borderColor} bg={panelBgColor} flexShrink={0}>
                <Tab 
                  flex={1} 
                  fontSize="xs" 
                  fontWeight="semibold"
                  color={mutedColor}
                  px={2}
                  _selected={{ 
                    bg: tabSelectedBg, 
                    color: "blue.600",
                    borderBottomColor: "blue.500"
                  }}
                  _hover={{
                    bg: tabHoverBg,
                    color: textColor
                  }}
                >
                  <Icon as={InfoIcon} mr={1} boxSize={3} />
                  Description
                </Tab>
                <Tab 
                  flex={1} 
                  fontSize="xs" 
                  fontWeight="semibold"
                  color={mutedColor}
                  px={2}
                  _selected={{ 
                    bg: tabSelectedBg, 
                    color: "green.600",
                    borderBottomColor: "green.500"
                  }}
                  _hover={{
                    bg: tabHoverBg,
                    color: textColor
                  }}
                >
                  <Icon as={CheckCircleIcon} mr={1} boxSize={3} />
                  Results
                </Tab>
                <Tab 
                  flex={1} 
                  fontSize="xs" 
                  fontWeight="semibold"
                  color={mutedColor}
                  px={2}
                  _selected={{ 
                    bg: tabSelectedBg, 
                    color: "purple.600",
                    borderBottomColor: "purple.500"
                  }}
                  _hover={{
                    bg: tabHoverBg,
                    color: textColor
                  }}
                >
                  <Icon as={ChatIcon} mr={1} boxSize={3} />
                  History
                </Tab>
                <Tab 
                  flex={1} 
                  fontSize="xs" 
                  fontWeight="semibold"
                  color={mutedColor}
                  px={2}
                  _selected={{ 
                    bg: tabSelectedBg, 
                    color: "orange.600",
                    borderBottomColor: "orange.500"
                  }}
                  _hover={{
                    bg: tabHoverBg,
                    color: textColor
                  }}
                >
                  <Icon as={ViewIcon} mr={1} boxSize={3} />
                  Assembly
                </Tab>
              </TabList>

              <TabPanels flex={1} overflow="hidden" minH={0}>
                {/* Description Tab */}
                <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                  <ProblemDescription content={decodedReadme} />
                </TabPanel>

                {/* Results Tab */}
                <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                  <VStack spacing={4} align="stretch" flex={1}>
                    <TestResults problemId={problemId!} response={submissionResponse} sourceCode={sourceCode} />
                    <StdoutDisplay response={submissionResponse} />
                  </VStack>
                </TabPanel>

                {/* History Tab */}
                <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                    <SubmissionHistory 
                      problemId={problemId!}
                      onRestoreSubmission={handleRestoreSubmission}
                    />
                </TabPanel>

                {/* Compiler Explorer Tab */}
                <TabPanel h="100%" overflow="auto" p={4} display="flex" flexDirection="column" bg={bgColor}>
                  <CompilerExplorer 
                    sourceCode={sourceCode}
                    compilerOptions={decodedCompilerOptions}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Command Palette */}
      <CommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={onCommandPaletteClose}
        commands={commands}
      />
    </Box>
  )
} 